import { appendClarificationChoice, ClarificationChoices, QueryMessage } from "./QueryMessage";
import React, { useEffect, useRef, useState } from "react";
import { IXSifterUser } from "../../types/XSifterAppUser";
import { ISampledPostItem, ISampledPostItemCollection, ISampleQueryRound, IXSifterSamplingProgress, QueryRefinementResult, QueryRefinementResultList } from "../../types/XSifterQueries";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, LinearProgress, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router";
import XSifterQueryService from "../../services/XSifterQueryService";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setDisplayMessage, setDisplayMessageType, setCurrentQueryId } from "../../store/XSifterAppReducer";
import "./XSifterQueryRevision.css";
import XSifterRevisionWorkflow from "./XSifterRevisionWorkflow";
import { AxiosError } from "axios";
import { NOT_RELEVANT_LABEL, RELEVANT_LABEL } from "./XSifterCodableItem";
import { tryGetErrorMessage } from "../../types/XSifterGeneralTypes";

interface IXSifterRevisionProps {
    savedQueryId: string | null,
    currentUser: IXSifterUser | undefined,
    onForceHeaderRemount: () => void
}

const XSifterRevision: React.FC<IXSifterRevisionProps> = (props: IXSifterRevisionProps) => {
    const [sampleItemCollection, setSampleItemCollection] = useState<ISampledPostItemCollection | null>(null);
    const [currentXQuery, setCurrentXQuery] = useState<string | null>(null);
    const [codingRoundId, setCodingRoundId] = useState<string | null>(null);
    const [codingRound, setCodingRound] = useState<ISampleQueryRound | null>(null);
    const [isSamplingItems, setIsSamplingItems] = useState<boolean>(false);
    const [wasSamplingCancelled, setWasSamplingCancelled] = useState<boolean>(false);
    const [isSamplingComplete, setIsSampingComplete] = useState<boolean>(false);
    const [samplingProgress, setSamplingProgress] = useState<IXSifterSamplingProgress>();
    const [currentSampleIndex, setCurrentSampleIndex] = useState<number>(0);
    const [showRefinementNoteDialog, setShowRefinementNoteDialog] = useState<boolean>(false);
    const [refinementNote, setRefinementNote] = useState<string>("");
    const [refinementClarificationNeeded, setRefinementClarificationNeeded] = useState<string | null>(null);
    const [refinementClarificationChoices, setRefinementClarificationChoices] = useState<string[]>([]);
    const [isSubmittingRefinementNote, setIsSubmittingRefinementNote] = useState<boolean>(false);
    const [showRefinementChoicesDialog, setShowRefinementChoicesDialog] = useState<boolean>(false);
    const [refinementChoices, setRefinementChoices] = useState<QueryRefinementResult[]>([]);
    const [selectedRefinementChoice, setSelectedRefinementChoice] = useState<number | null>(null);
    const [isApplyingRefinement, setIsApplyingRefinement] = useState<boolean>(false);
    const queryService = new XSifterQueryService();
    const abortControllerRef = useRef<AbortController | null>(null);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();    
    const [queryParams] = useSearchParams();
    const paramCodingRoundId = queryParams.get("codingRoundId");
    const hasRunRef = useRef(false);
    const hasStartedSamplingRef = useRef(false);

    queryService.setQueryTraceId(useAppSelector((state) => state.app.currentQueryId));

    useEffect(() => {
        if (hasRunRef.current)
            return;

        if (!props.savedQueryId)
            return;

        hasRunRef.current = true;

        if (paramCodingRoundId) {
            queryService.getCodingRound(paramCodingRoundId)
                .then((response) => {
                    queryService.setQueryTraceId(response.currentQueryId);
                    dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
                    // get or create coding round
                    setCodingRound(response);
                    setCodingRoundId(response.codingRoundId);
                    fetchTranslatedQuery();
                }).catch((error: AxiosError) => {
                    dispatch(setDisplayMessage(error.message));
                    dispatch(setDisplayMessageType("error"))
                    navigate(`/app/query?queryId=${props.savedQueryId}`);
                });
            return;
        }

        queryService.getOrCreateCodingRound(props.savedQueryId ?? "")
            .then((response) => {
                // get or create coding round
                queryService.setQueryTraceId(response.currentQueryId);
                dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
                setCodingRound(response);
                setCodingRoundId(response.codingRoundId);
                fetchTranslatedQuery();
            }).catch((error: AxiosError) => {
                dispatch(setDisplayMessage(error.message));
                dispatch(setDisplayMessageType("error"))
                navigate(`/app/query?queryId=${props.savedQueryId}`);
            });

    }, [props.savedQueryId]);

    useEffect(() => {
        if (!codingRoundId)
            return;

        if (isSamplingItems)
            return;

        if (wasSamplingCancelled)
            return;

        if (isSamplingComplete) {
            // load our set of results here...
            queryService.getSampledItemsForCoding(codingRoundId)
                .then((response) => {
                    setSampleItemCollection(response);
                    setCurrentSampleIndex(0);
                }).catch((error: AxiosError) => {
                    dispatch(setDisplayMessage(error.message));
                    dispatch(setDisplayMessageType("error"))
                    navigate(`/app/query?queryId=${props.savedQueryId}`);
                });
        }
    }, [isSamplingItems, wasSamplingCancelled, isSamplingComplete])

    const fetchTranslatedQuery = () => {
        queryService.translateSavedQuery(props.savedQueryId ?? "")
            .then((translated: any) => {
                setCurrentXQuery(translated.query);
            }).catch((error) => {
                dispatch(setDisplayMessage(error));
                dispatch(setDisplayMessageType("error"))
                navigate(`/app/query?queryId=${props.savedQueryId}`);
            })
    }

    const onSampleItemMessage = (message: IXSifterSamplingProgress) => {
        if (message.hasError) {
            dispatch(setDisplayMessage(message.message));
            dispatch(setDisplayMessageType("error"))
            navigate(`/app/query?queryId=${props.savedQueryId}`);
            return;
        }
        if (message.isComplete) {
            setIsSampingComplete(true);
        }
        setSamplingProgress(message);
    }

    const onSampleError = (error: string) => {
        if (abortControllerRef?.current?.signal.aborted) {
            setSampleItemCollection(null);
            setWasSamplingCancelled(true);
            console.log(`Stream was manually cancelled`);
            dispatch(setDisplayMessage("Sampling process was cancelled."));
            dispatch(setDisplayMessageType("warning"))
            navigate(`/app/query?queryId=${props.savedQueryId}`);
            return;
        }
        dispatch(setDisplayMessage(error));
        dispatch(setDisplayMessageType("error"))
        navigate(`/app/query?queryId=${props.savedQueryId}`);
    }

    const onCompleteSample = () => {
        setIsSamplingItems(false);
        abortControllerRef.current = null;
    }

    const sampleItemFromQuery = () => {
        if (!codingRoundId)
            return;

        // clear out any cancellation from a previous attempt - otherwise the completion effect's
        // wasSamplingCancelled guard permanently blocks handing off to the coding screen, even once
        // this new attempt finishes successfully.
        setWasSamplingCancelled(false);
        setIsSamplingItems(true);
        abortControllerRef.current = new AbortController();
        queryService.fetchSampleQueryItems(codingRoundId, abortControllerRef.current, onSampleItemMessage, onSampleError, onCompleteSample);
    };

    // sampling starts automatically once the coding round and translated query are ready
    useEffect(() => {
        if (!codingRoundId || !currentXQuery || hasStartedSamplingRef.current)
            return;

        hasStartedSamplingRef.current = true;
        sampleItemFromQuery();
    }, [codingRoundId, currentXQuery]);

    if (!currentXQuery) {
        return (<>... loading ...</>);
    }

    if ((sampleItemCollection?.items?.length ?? 0) === 0) {
        return (
            <Box sx={{ padding: "24px", maxWidth: 700, margin: "0 auto" }}>
                <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: "8px" }}>Sampling Data...</Typography>
                <Typography variant="body2" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
                    Please do not close this window while sampling is in progress.
                </Typography>
                <Box sx={{ width: "100%", marginBottom: "16px" }}>
                    <LinearProgress
                        variant={samplingProgress?.percentComplete === 1.0 ? "indeterminate" : "determinate"}
                        value={(samplingProgress?.percentComplete || 0) * 100.0}
                        sx={{ height: 4, borderRadius: 2 }}
                    />
                    <div>
                        {samplingProgress?.message}
                    </div>
                </Box>
                <Button
                    variant="outlined"
                    onClick={() => (abortControllerRef.current && abortControllerRef.current.abort())}>
                    Cancel
                </Button>
            </Box>
        );
    }

    // we have samples - do our coding process...
    const onScoreCurrentItem = (item: ISampledPostItem, label: string): Promise<boolean> => 
    {
        return new Promise<boolean>((resolve, reject) => {
            if (!sampleItemCollection?.items)
                resolve(false);

            let foundItem = sampleItemCollection?.items.find((element) => element.codedSampleId === item.codedSampleId);
            if (!foundItem)
                resolve(false);
            // resolve true if more items to code
            let codingWeight = (label === RELEVANT_LABEL ? 1 : label === NOT_RELEVANT_LABEL ? -1 : 0);
            if (codingWeight === 0) {
                resolve(true);
                return;
            }

            queryService.scoreSampledItem(item.codingRoundId, item.codedSampleId, codingWeight)
                .then((response: any) => {
                    if (foundItem)
                        foundItem.label = label ?? "";
                    resolve(true);
                }).catch((error: any) => {
                    reject(error);
                });
        });
    }

    const onCancelWorkflow = () => {
        navigate(`/app/query?queryId=${props.savedQueryId}`);
    }

    const onUpdateSampleItems = (updater: (items: ISampledPostItem[]) => ISampledPostItem[]) => {
        setSampleItemCollection(prev => prev ? { ...prev, items: updater(prev.items) } : prev);
    }

    const onSubmitForRefinement = () => {
        if (!codingRoundId)
            return;

        setRefinementNote("");
        setRefinementClarificationNeeded(null);
        setRefinementClarificationChoices([]);
        setShowRefinementNoteDialog(true);
    }

    const onCancelRefinementNoteDialog = () => {
        if (isSubmittingRefinementNote)
            return;

        setShowRefinementNoteDialog(false);
        setRefinementClarificationNeeded(null);
        setRefinementClarificationChoices([]);
        const twttr = (window as any).twttr;
        if (twttr && typeof twttr.widgets.load === 'function')
            twttr.widgets.load();
    }

    const onConfirmRefinementNoteDialog = () => {
        if (!codingRoundId)
            return;

        setIsSubmittingRefinementNote(true);

        queryService.submitForRefinement({
            codingRoundId: codingRoundId,
            notes: refinementNote.trim() || ""
        }).then((response: QueryRefinementResultList) => {
            queryService.setQueryTraceId(response.currentQueryId);
            dispatch(setCurrentQueryId(queryService.getQueryTraceId()));
            setIsSubmittingRefinementNote(false);

            if (response.needsClarification) {
                // the LLM needs more information before it can propose refinements - keep the
                // notes dialog open (with whatever the user already typed) and show why, so they
                // can add to it and resubmit rather than being dropped into an empty choices list.
                setRefinementClarificationNeeded(response.needsClarification);
                setRefinementClarificationChoices(response.clarificationChoices ?? []);
                setShowRefinementChoicesDialog(false);
                setShowRefinementNoteDialog(true);
                return;
            }

            setRefinementClarificationNeeded(null);
            setRefinementClarificationChoices([]);
            setShowRefinementNoteDialog(false);
            setRefinementChoices(response.results ?? []);
            setSelectedRefinementChoice(null);
            setShowRefinementChoicesDialog(true);
        }).catch((error: any) => {
            var asError = tryGetErrorMessage(error);
            setIsSubmittingRefinementNote(false);
            setShowRefinementNoteDialog(false);
            setShowRefinementChoicesDialog(false);
            dispatch(setDisplayMessage(asError.error?.errorMessage ?? "an unknown error occurred"));
            dispatch(setDisplayMessageType("error"))
        });
    }

    const onUpdateRefinementRequest = (choice: string | null) => {
        if (choice)
            setRefinementNote((current) => appendClarificationChoice(current, choice).slice(0, 256));
        setRefinementClarificationNeeded(null);
        setRefinementClarificationChoices([]);
    }

    const onCancelRefinementChoicesDialog = () => {
        if (isSubmittingRefinementNote)
            return;

        setShowRefinementChoicesDialog(false);
        setRefinementChoices([]);
        setSelectedRefinementChoice(null);
    }

    const onConfirmRefinementChoice = () => {
        if (selectedRefinementChoice === null || !codingRoundId)
            return;

        const chosenRefinement = refinementChoices[selectedRefinementChoice];

        setIsApplyingRefinement(true);
        queryService.applyQueryRefinement(props.savedQueryId ?? "", chosenRefinement)
            .then(() => {
                setIsApplyingRefinement(false);
                setShowRefinementChoicesDialog(false);
                dispatch(setDisplayMessage("Query was refined successfully"));
                dispatch(setDisplayMessageType("success"));
                navigate(`/app/query?queryId=${props.savedQueryId}`, { state: { refined: true } });
            }).catch((error: any) => {
                setIsApplyingRefinement(false);
                dispatch(setDisplayMessage(error));
                dispatch(setDisplayMessageType("error"))
            });
    }

    // the choices are shown until the user picks one and clicks "Update request"
    const awaitingClarificationChoice = !!refinementClarificationNeeded && refinementClarificationChoices.length > 0;

    return (
        <>
            {/* kept mounted (hidden) while the refinement choices are shown, so its state is preserved */}
            <Box sx={{ display: showRefinementChoicesDialog ? "none" : "block" }}>
            <XSifterRevisionWorkflow
                codingRoundId={codingRoundId ?? ""} 
                roundNumber={codingRound?.roundNumber ?? 0} 
                sampleItemCollection={sampleItemCollection}
                onForceHeaderRemount={props.onForceHeaderRemount}
                onScoreCurrentItem={onScoreCurrentItem}
                onCancelWorkflow={onCancelWorkflow}
                onSubmitForRefinement={onSubmitForRefinement}
                onUpdateSampleItems={onUpdateSampleItems}
             />
            </Box>
            <Dialog open={showRefinementNoteDialog} onClose={onCancelRefinementNoteDialog} fullWidth maxWidth="sm">
                <DialogTitle>Refine Query</DialogTitle>
                <Box sx={{ position: "relative" }}>
                    <DialogContent>
                        {refinementClarificationNeeded && (
                            <QueryMessage
                                color="warning"
                                title="We need a bit of clarification"
                                message={refinementClarificationNeeded}
                            >
                                <ClarificationChoices
                                    choices={refinementClarificationChoices}
                                    disabled={isSubmittingRefinementNote}
                                    onUpdateRequest={onUpdateRefinementRequest}
                                />
                            </QueryMessage>
                        )}
                        <DialogContentText sx={{ marginBottom: "16px" }}>
                            Optionally add any notes about the samples you coded.
                            Describe the kinds of posts you want to see more (or less) of, e.g. &quot;I want first-person accounts of side effects, not news articles or ads.&quot;
                        </DialogContentText>
                        <TextField
                            autoFocus
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={6}
                            label="Notes (optional)"
                            placeholder="Enter your notes or suggestions for the model to refine the query."
                            value={refinementNote}
                            onChange={(event) => setRefinementNote(event.target.value.slice(0, 256))}
                            slotProps={{ htmlInput: { maxLength: 256 } }}
                            helperText={`${refinementNote.length}/256`}
                            disabled={isSubmittingRefinementNote}
                        />
                    </DialogContent>
                    {isSubmittingRefinementNote && (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
                <DialogActions>
                    <Button onClick={onCancelRefinementNoteDialog} disabled={isSubmittingRefinementNote}>Cancel</Button>
                    <Button onClick={onConfirmRefinementNoteDialog} variant="contained" disabled={isSubmittingRefinementNote || awaitingClarificationChoice}>Refine Query</Button>
                </DialogActions>
            </Dialog>
            {showRefinementChoicesDialog && (
                <Box sx={{ position: "relative", padding: "24px", maxWidth: 1100, margin: "0 auto" }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: "8px" }}>Choose a Refined Query</Typography>
                    <Typography variant="body1" sx={{ marginBottom: "20px" }}>
                        Select one of the suggested refinements below, or cancel to keep your current query.
                    </Typography>
                    <RadioGroup
                        value={selectedRefinementChoice !== null ? String(selectedRefinementChoice) : ""}
                        onChange={(event) => setSelectedRefinementChoice(Number(event.target.value))}
                    >
                        {refinementChoices.map((choice, index) => (
                            <Box
                                key={index}
                                sx={{
                                    border: "1px solid #888888",
                                    borderRadius: "10px",
                                    padding: "16px",
                                    marginBottom: "16px",
                                }}
                            >
                                <FormControlLabel
                                    value={String(index)}
                                    control={<Radio sx={{ alignSelf: "flex-start" }} />}
                                    disabled={isSubmittingRefinementNote}
                                    sx={{ alignItems: "flex-start", width: "100%", margin: 0 }}
                                    label={
                                        <Box sx={{ marginLeft: "4px" }}>
                                            <Typography component="div" variant="h6" sx={{ fontWeight: "bold" }}>
                                                {choice.intent}
                                            </Typography>
                                            <blockquote className="translated-query">{choice.query}</blockquote>
                                            <Typography component="div" variant="body1" sx={{ color: "text.secondary" }}>
                                                {choice.rationale}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        ))}
                    </RadioGroup>
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: "8px" }}>
                        <Button onClick={onCancelRefinementChoicesDialog} disabled={isApplyingRefinement || isSubmittingRefinementNote}>Cancel</Button>
                        <Button onClick={onConfirmRefinementNoteDialog} disabled={isApplyingRefinement || isSubmittingRefinementNote}>Get New Suggestions</Button>
                        <Button
                            onClick={onConfirmRefinementChoice}
                            variant="contained"
                            disabled={selectedRefinementChoice === null || isApplyingRefinement || isSubmittingRefinementNote}
                        >
                            Use Selected Query
                        </Button>
                    </Box>
                    {isSubmittingRefinementNote && (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255, 255, 255, 0.7)",
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
}

export default XSifterRevision;
